import { useTranslation } from 'react-i18next';
import { Typography } from '@wbk/ui';
import { twMerge } from 'tailwind-merge';

type Props = {
  /** Price to display */
  price: number | undefined | null;
  /** Price before discount (optional) */
  originalPrice?: number;
  /** Additional CSS classes */
  className?: string;
  /** CSS classes for price text */
  priceClassName?: string;
  /** CSS classes for currency symbol */
  currencyClassName?: string;

  /** Event type to determine special pricing rules (optional) */
  eventType?: string;
};

// SAR currency configuration
const SAR_CURRENCY = {
  symbol: 'SAR',
  svgPath: '/icons/SAR.svg',
  direction: 'left' as const,
};

export const CurrencyLabel = ({
  price,
  originalPrice,
  className,
  priceClassName,
  currencyClassName,
  eventType,
}: Props) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar');

  const hasDiscount = originalPrice && originalPrice > (price || 0);
  const displayPrice = (hasDiscount ? price : price) || 0;

  // Format price based on locale
  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat(i18n.language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <span className={twMerge('flex items-center gap-1', className)}>
      {!price || price <= 0 ? (
        <Typography as='span' className='text-green-500'>
          {price === 0
            ? i18n.t('common:free', 'Free')
            : eventType && eventType?.toLowerCase().includes('offline')
              ? i18n.t('common:comingsoon', 'Coming Soon')
              : ''}
        </Typography>
      ) : (
        <Typography
          as='span'
          className={twMerge(
            'flex items-center gap-1',
            !isRTL ? 'flex-row-reverse' : 'flex-row',
            priceClassName
          )}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          {/* Original price with strikethrough if discount exists */}
          {hasDiscount && (
            <Typography as='span' className='text-gray-500 line-through' color='text-secondary'>
              {formatPrice(originalPrice!)}
            </Typography>
          )}

          {/* Current price */}
          <Typography as='span'>{formatPrice(displayPrice)}</Typography>
          {/* SAR Currency Symbol */}
          <span className={twMerge('flex items-center', currencyClassName)}>
            <img
              src={SAR_CURRENCY.svgPath}
              alt='SAR'
              width='14'
              height='14'
              className='text-current'
            />
          </span>
        </Typography>
      )}
    </span>
  );
};
